import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Printer } from 'lucide-react';
import { formatCurrency, formatDateTime } from './posUtils';

const ShiftReportDialog = ({
  open,
  onOpenChange,
  shiftReport,
}) => {
  const handlePrint = () => {
    if (!shiftReport) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Shift Report - ${shiftReport.shiftNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            h1 { text-align: center; font-size: 18px; margin-bottom: 5px; }
            h2 { text-align: center; font-size: 14px; color: #666; margin-bottom: 20px; }
            .info { text-align: center; font-size: 12px; color: #666; margin-bottom: 15px; }
            .section { margin-bottom: 15px; }
            .row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px dotted #ccc; font-size: 12px; }
            .row:last-child { border-bottom: none; }
            .row.total { font-weight: bold; font-size: 14px; border-top: 2px solid #000; margin-top: 5px; padding-top: 10px; }
            .section-title { font-weight: bold; font-size: 12px; border-bottom: 1px solid #000; padding-bottom: 5px; margin-bottom: 10px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #999; }
            .diff-over { color: #b45309; }
            .diff-short { color: #dc2626; }
            .diff-exact { color: #16a34a; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Nenen's Agrivet Supply - SHIFT REPORT</h1>
          <h2>${shiftReport.shiftNumber || ''}</h2>
          <div class="info">
            <p>Cashier: ${shiftReport.cashierName || ''}</p>
            <p>Opened: ${shiftReport.openedAt ? new Date(shiftReport.openedAt).toLocaleString('en-PH') : ''}</p>
            <p>Closed: ${shiftReport.closedAt ? new Date(shiftReport.closedAt).toLocaleString('en-PH') : ''}</p>
          </div>
          
          <div class="section">
            <div class="section-title">SALES SUMMARY</div>
            <div class="row"><span>Total Transactions</span><span>${shiftReport.totalTransactions || 0}</span></div>
            <div class="row"><span>Cash Sales</span><span>₱${(shiftReport.cashSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
            <div class="row"><span>GCash Sales</span><span>₱${(shiftReport.gcashSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
            <div class="row"><span>Credit Sales</span><span>₱${(shiftReport.creditSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
            <div class="row total"><span>TOTAL SALES</span><span>₱${(shiftReport.totalSales || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
          </div>
          
          <div class="section">
            <div class="section-title">CASH DRAWER</div>
            <div class="row"><span>Opening Cash</span><span>₱${(shiftReport.openingCash || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
            <div class="row"><span>Expected Cash</span><span>₱${(shiftReport.expectedCash || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
            <div class="row"><span>Closing Cash</span><span>₱${(shiftReport.closingCash || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></div>
            <div class="row total ${(shiftReport.cashDifference || 0) < 0 ? 'diff-short' : (shiftReport.cashDifference || 0) > 0 ? 'diff-over' : 'diff-exact'}">
              <span>Difference</span>
              <span>${(shiftReport.cashDifference || 0) >= 0 ? '+' : ''}₱${(shiftReport.cashDifference || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Printed: ${new Date().toLocaleString('en-PH')}</p>
            <p>Nenen's Agrivet Supply</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>End of Shift Report</DialogTitle>
        </DialogHeader>
        {shiftReport && (
          <div className="space-y-4" id="shift-report">
            <div className="text-center border-b pb-4">
              <h3 className="font-bold text-lg">NAS - POS Shift Report</h3>
              <p className="text-sm text-slate-500">{shiftReport.shiftNumber}</p>
              <p className="text-sm text-slate-500">Cashier: {shiftReport.cashierName}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Opened</p>
                <p className="font-medium">{formatDateTime(shiftReport.openedAt)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Closed</p>
                <p className="font-medium">{formatDateTime(shiftReport.closedAt)}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Transactions</span>
                <span className="font-bold">{shiftReport.totalTransactions}</span>
              </div>
              <div className="flex justify-between">
                <span>Cash Sales</span>
                <span>{formatCurrency(shiftReport.cashSales)}</span>
              </div>
              <div className="flex justify-between">
                <span>GCash Sales</span>
                <span>{formatCurrency(shiftReport.gcashSales)}</span>
              </div>
              <div className="flex justify-between">
                <span>Credit Sales</span>
                <span>{formatCurrency(shiftReport.creditSales)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total Sales</span>
                <span className="text-emerald-600">{formatCurrency(shiftReport.totalSales)}</span>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Opening Cash</span>
                <span>{formatCurrency(shiftReport.openingCash)}</span>
              </div>
              <div className="flex justify-between">
                <span>Expected Cash</span>
                <span>{formatCurrency(shiftReport.expectedCash)}</span>
              </div>
              <div className="flex justify-between">
                <span>Closing Cash</span>
                <span>{formatCurrency(shiftReport.closingCash)}</span>
              </div>
              <div className={`flex justify-between font-bold ${
                shiftReport.cashDifference < 0 ? 'text-red-600' : 
                shiftReport.cashDifference > 0 ? 'text-amber-600' : 'text-green-600'
              }`}>
                <span>Difference</span>
                <span>{shiftReport.cashDifference >= 0 ? '+' : ''}{formatCurrency(shiftReport.cashDifference)}</span>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
            <Printer size={18} className="mr-2" />
            Print Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftReportDialog;
